import { FaMobileAlt, FaCheckCircle, FaFileAlt, FaClock, FaShieldAlt, FaHandHoldingUsd } from "react-icons/fa";
import { useInView } from "../hooks/useInView";
import { useState } from "react";

const WhyChooseUs = () => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const features = [
    {
      icon: <FaMobileAlt />,
      title: "Fully Digital",
      description: "You can apply anytime and from anywhere without visiting a branch.",
      color: "from-[#21A6B5] to-[#3D4A7C]",
      bgGradient: "from-[#E0F7FF] to-[#B3E5FC]",
    },
    {
      icon: <FaCheckCircle />,
      title: "Instant Approval",
      description: "Get an approval right away without a long waiting time.",
      color: "from-[#4CAF50] to-[#2E7D32]",
      bgGradient: "from-[#E8F5E9] to-[#C8E6C9]",
    },
    {
      icon: <FaFileAlt />,
      title: "Minimal Documentation",
      description: "The process is simple and only needs a few basic documents.",
      color: "from-[#FF9800] to-[#F57C00]",
      bgGradient: "from-[#FFF3E0] to-[#FFE0B2]",
    },
    {
      icon: <FaClock />,
      title: "Quick Disbursal",
      description: "Funds are transferred to your account without delay.",
      color: "from-[#9C27B0] to-[#6A1B9A]",
      bgGradient: "from-[#F3E5F5] to-[#E1BEE7]",
    },
    {
      icon: <FaHandHoldingUsd />,
      title: "No Collateral Required",
      description: "No need to pledge assets or security.",
      color: "from-[#00BCD4] to-[#0097A7]",
      bgGradient: "from-[#E0F7FA] to-[#B2EBF2]",
    },
    {
      icon: <FaShieldAlt />,
      title: "Safe and Secure",
      description: "Your information is protected by reliable security systems.",
      color: "from-[#F44336] to-[#C62828]",
      bgGradient: "from-[#FFEBEE] to-[#FFCDD2]",
    },
  ];

  return (
    <section ref={ref} className="w-full py-16 md:py-20 lg:py-24 bg-gradient-to-br from-white via-[#F0FFFE] to-[#E0F9F7] relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-[#A9F9EE] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-[#7DD4D0] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-[#21A6B5] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header */}
        <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block mb-4">
            <span className="px-6 py-2 bg-gradient-to-r from-[#21A6B5] to-[#3D4A7C] text-white text-sm md:text-base font-semibold rounded-full shadow-lg animate-pulse-glow">
              ✨ Our Key Benefits
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-[60px] lg:leading-[60px] font-bold text-center text-[#2C3E50] mb-4 font-['Inter']">
            Why Choose Us?
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-600 font-['Inter'] max-w-3xl mx-auto">
            Experience the future of lending with our innovative, customer-first approach
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          {features.map((item, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group relative bg-white rounded-3xl p-8 md:p-10 lg:p-12 shadow-xl hover:shadow-2xl transition-all duration-700 cursor-pointer overflow-hidden ${
                isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              } ${
                hoveredIndex === index ? 'scale-105 -translate-y-2' : ''
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Animated Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${item.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon Container with Gradient */}
                <div className={`w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-3xl md:text-4xl lg:text-5xl mb-6 md:mb-8 shadow-lg transform transition-all duration-500 ${
                  hoveredIndex === index ? 'rotate-12 scale-110' : 'rotate-0 scale-100'
                }`}>
                  <div className={`transition-transform duration-500 ${
                    hoveredIndex === index ? 'scale-125' : 'scale-100'
                  }`}>
                    {item.icon}
                  </div>
                </div>

                {/* Title */}
                <h3 className={`text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-5 font-['Inter'] transition-colors duration-300 ${
                  hoveredIndex === index ? 'text-[#3D4A7C]' : 'text-[#2C3E50]'
                }`}>
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-gray-700 text-base md:text-lg lg:text-xl leading-relaxed font-['Inter'] transition-colors duration-300 group-hover:text-gray-800">
                  {item.description}
                </p>

                {/* Animated Border */}
                <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${item.color} transition-all duration-500 ${
                  hoveredIndex === index ? 'w-full' : 'w-0'
                }`}></div>
              </div>

              {/* Corner Decoration */}
              <div className={`absolute top-0 right-0 w-20 h-20 bg-[#21A6B5] opacity-10 rounded-bl-full transition-all duration-500 ${
                hoveredIndex === index ? 'scale-150' : 'scale-100'
              }`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-12 md:mt-16 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: '600ms' }}>
          <p className="text-lg md:text-xl text-gray-600 font-['Inter'] mb-6">
            Ready to experience hassle-free lending?
          </p>
          <a
            href={import.meta.env.VITE_WEBSITE_LINK}
            target="_blank"
            className="inline-block px-10 py-4 bg-[#3D4A7C] hover:bg-[#2d3a6c] text-white text-base leading-6 font-semibold font-['Inter'] rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 animate-pulse-glow"
          >
            Apply Now →
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
